import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layers } from "lucide-react";
import GlassCard from "../components/GlassCard";
import GoldButton from "../components/GoldButton";
import GhostButton from "../components/GhostButton";
import AuditDetailModal from "../components/AuditDetailModal";
import { getAudit, listAudits } from "../lib/audits";
import { cn } from "../lib/utils";

const PAGE_SIZE = 10;

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ScoreCell({ audit }) {
  const { t } = useTranslation();
  if (audit.bounced) {
    return (
      <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/[0.08] px-2 py-0.5 text-[11px] font-medium text-amber-300">
        {t("history.bounce")}
      </span>
    );
  }
  const s = audit.nexa_score;
  const color = s >= 9 ? "#D4AF37" : s >= 7 ? "#1E40AF" : "#7F1D1D";
  return (
    <span className="font-display text-lg tabular-nums" style={{ color }}>
      {typeof s === "number" ? s.toFixed(1) : "—"}
    </span>
  );
}

export default function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [mode, setMode] = useState("all");
  const [bouncedFilter, setBouncedFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    const filters = {
      page,
      pageSize: PAGE_SIZE,
      mode: mode === "all" ? undefined : mode,
      bounced:
        bouncedFilter === "all" ? undefined : bouncedFilter === "yes" ? true : false,
    };
    const res = await listAudits(filters);
    setLoading(false);
    if (res.ok) setData(res.data);
  }, [page, mode, bouncedFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [mode, bouncedFilter]);

  const openDetail = async (id) => {
    const res = await getAudit(id);
    if (res.ok) setDetail(res.data);
  };

  const onReaudit = (audit) => {
    try {
      sessionStorage.setItem("nxa_reaudit_prompt", audit.prompt || "");
    } catch {
      /* ignore */
    }
    setDetail(null);
    navigate("/auditor");
  };

  const modeOptions = ["all", "quick", "deep"];
  const bouncedOptions = ["all", "yes", "no"];

  return (
    <section className="grid gap-6" data-testid="history-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
            {t("history.title")}
          </h1>
          <p className="mt-1 text-sm text-white/55">{t("history.subtitle")}</p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3" data-testid="history-filters">
        <FilterGroup
          label={t("history.filter.mode")}
          options={modeOptions}
          value={mode}
          onChange={setMode}
          renderOption={(o) =>
            o === "all" ? t("history.filter.all") : t(`history.filter.${o}`)
          }
          testId="history-filter-mode"
        />
        <FilterGroup
          label={t("history.filter.bounced")}
          options={bouncedOptions}
          value={bouncedFilter}
          onChange={setBouncedFilter}
          renderOption={(o) =>
            o === "all"
              ? t("history.filter.all")
              : o === "yes"
                ? t("history.filter.bouncedYes")
                : t("history.filter.bouncedNo")
          }
          testId="history-filter-bounced"
        />
      </div>

      <GlassCard className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-white/55">{t("common.loading")}</div>
        ) : data.items.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-4 p-12 text-center"
            data-testid="history-empty"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06]">
              <Layers className="h-5 w-5 text-[#D4AF37]" />
            </span>
            <p className="text-sm text-white/55 max-w-sm">{t("history.empty")}</p>
            <Link to="/auditor">
              <GoldButton data-testid="history-empty-cta">{t("history.emptyCta")}</GoldButton>
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="history-list">
            {data.items.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => openDetail(a.id)}
                  className="flex w-full flex-wrap items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
                  data-testid={`history-row-${a.id}`}
                >
                  <div className="min-w-[170px] text-xs text-white/45">
                    {formatDate(a.created_at)}
                  </div>
                  <div className="min-w-[70px]">
                    <ModePill mode={a.mode} />
                  </div>
                  <div className="w-14 text-center">
                    <ScoreCell audit={a} />
                  </div>
                  <div className="flex-1 text-sm text-white/80 truncate">
                    {(a.prompt || "").slice(0, 80)}
                    {(a.prompt || "").length > 80 ? "…" : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      {data.items.length > 0 && (
        <div className="flex items-center justify-between text-sm text-white/55">
          <span>{t("history.page", { page: data.page, pages: totalPages })}</span>
          <div className="flex gap-2">
            <GhostButton
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page <= 1}
              data-testid="history-prev"
            >
              {t("history.prev")}
            </GhostButton>
            <GhostButton
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={data.page >= totalPages}
              data-testid="history-next"
            >
              {t("history.next")}
            </GhostButton>
          </div>
        </div>
      )}

      <AuditDetailModal
        open={!!detail}
        audit={detail}
        onClose={() => setDetail(null)}
        onReaudit={onReaudit}
      />
    </section>
  );
}

function ModePill({ mode }) {
  const color = mode === "deep" ? "#1E40AF" : "#D4AF37";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider"
      style={{
        borderColor: color + "55",
        color: color,
        background: color + "12",
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {mode}
    </span>
  );
}

function FilterGroup({ label, options, value, onChange, renderOption, testId }) {
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      <span className="text-[11px] uppercase tracking-[0.18em] text-white/40">
        {label}
      </span>
      <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-0.5">
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(o)}
              className={cn(
                "rounded-full px-3 py-1 text-xs transition-colors",
                active ? "bg-white/[0.08] text-white" : "text-white/60 hover:text-white",
              )}
              data-testid={`${testId}-${o}`}
              aria-pressed={active}
            >
              {renderOption(o)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
